import type { Emitter } from "@core/events/emitter";

/**
 * `IConsolePlacementModel` — web port of the Quest app's
 * `CampfireScenes/Assets/CampfireQuest/Runtime/Models/QuestConsolePlacementModel.cs`
 * (`IConsolePlacementModel`).
 *
 * ## What console placement IS
 *
 * "Put the table down over there in my room." The wearer aims at their real
 * floor and the whole Campfire STAGE — table, console, floor grid, scene
 * content, media cards, annotations and every remote participant — moves to
 * that spot in tracking space. It is the one feature that reconciles Campfire's
 * fixed scene origin with the physical room the headset is standing in.
 *
 * ## The transform this owns, and the ONE thing to understand before editing
 *
 * Unity moves a root `GameObject` literally named **"Space"**
 * (`BaseSceneModel.m_spaceRootTransform`, `m_spaceName = "Space"`), which is
 * `ISceneModel.RootSpace` and the parent of every per-scene space. The web's
 * exact counterpart is **`engine.root`** (`"campfire.root"`), which is likewise
 * the parent of `campfire-scenes`, `campfire.sceneEnvironment`,
 * `campfire-media-tiles`, `campfire-annotations` and `multiuserRoot`. This
 * model owns that transform's pose; `XRConsolePlacementController` applies it.
 *
 * **`RootSpace` is also the frame the multiuser wire speaks.** Unity publishes
 * the local head as `rootSpace.InverseTransformPoint(camera.position)` /
 * `Quaternion.Inverse(rootSpace.rotation) * camera.rotation`
 * (`QuestLocalUserModel.GetTransformData`, `:322`) and places peers with
 * `rootSpace.TransformPoint(localPos)` (`TransformInterpolator.UpdateTransform`,
 * called from `NetworkUsersModel.UpdateTransformInterpolator` — which
 * `QuestConsolePlacementModel.UpdateObjects` fires on every move). So a
 * `ParticipantTransform` is **stage-local**, never tracking-space, and two
 * clients whose stages sit in completely different rooms still agree about who
 * is standing where around the table.
 *
 * The web gets the RECEIVE half for free: peer avatars are children of
 * `multiuserRoot`, itself a child of `engine.root`, and their poses are written
 * as LOCAL positions — which is exactly `rootSpace.TransformPoint`. The SEND
 * half is not free: the camera lives in world space, so `ViewportView` must
 * convert through `runtime/xr/stageSpace.ts` before handing a pose to
 * `IMultiuserSessionModel.updateLocalTransform`. See that module's header for
 * the full list of what does and does not need converting.
 *
 * ## Deliberate deviations from Unity, do not "fix" back
 *
 *  - **Yaw-only rotation.** Unity stores a full `Quaternion` because the marker
 *    / spatial-anchor paths (`Move(Pose)`, `CoPlaceStage`) can hand it an
 *    arbitrary pose. Neither is ported (no OpenCV marker tracking, no
 *    `OVRSpatialAnchor` on the web), and every interactive path — hand aim,
 *    thumbstick `Rotate`, `InitialPlacement` — only ever produces a yaw. A
 *    yaw plus a position is therefore the whole reachable state, and keeping it
 *    that way makes "the stage is level" a property of the type rather than an
 *    invariant to maintain.
 *  - **Session-scoped, not persisted.** Unity ends every placement with
 *    `SaveSpatialAnchor()` so the console re-localizes in the same physical
 *    spot next launch. WebXR has no equivalent that works across the target
 *    browsers (Meta's persistent anchors are unexposed and visionOS has none),
 *    and a `local-floor` origin is not guaranteed stable between sessions — so
 *    a stored pose could put the table through a wall on the next entry.
 *    {@link reset} therefore runs on session exit AND on a recenter, which is
 *    the same rule `XRPanelsRenderer` follows for the slates.
 *  - **`InitialPlacement` is not ported.** It seats the stage 0.3 m in front of
 *    the Universal Menu at −45°, rotated by the multiuser seat angle
 *    (`NetworkUsersModel.GetInitialRotateAnglePlacement`, itself unported). The
 *    web instead spawns the USER back from a stage that stays at the origin
 *    (`XRSessionService`'s offset reference space), which is the equivalent
 *    arrangement reached from the other side. Placement is the wearer's
 *    adjustment on top of it.
 */

/**
 * Stage pose in TRACKING space (three world metres, Y up, yaw in degrees).
 *
 * ⚠ `yawDegrees` is a **three-space** yaw — right-handed about +Y, so a
 * positive value turns the stage COUNTER-clockwise seen from above. Unity's
 * `Transform.Rotate(Vector3.up, +d)` turns it the other way, because Unity is
 * left-handed; the port's canonical basis map spells the same rule out as
 * `M·Ry(θ)·M = Ry(−θ)` (`models/utils/unityBasis.ts`). Nothing here crosses to
 * Unity — the stage pose is never persisted and never reaches the wire, since
 * participant transforms are stage-LOCAL and carry no stage yaw — so the sign
 * is a UI concern only, and it is settled where the buttons are named
 * (`useConsolePlacement`). Do not "correct" it by negating in `applyStagePose`:
 * the renderer's convention is the right one for the renderer.
 */
export interface ConsoleStagePose {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yawDegrees: number;
}

export const IDENTITY_STAGE_POSE: ConsoleStagePose = { x: 0, y: 0, z: 0, yawDegrees: 0 };

export interface IConsolePlacementModel {
  /** Unity `ConsolePlacementModeChanged`. */
  readonly onPlacementModeChanged: Emitter<boolean>;
  /** Fires on every pose write, including the per-frame drag. */
  readonly onStagePoseChanged: Emitter<ConsoleStagePose>;
  /** {@link isAwaitingConfirmation} changed. Web-first, like the state itself. */
  readonly onAwaitingConfirmationChanged: Emitter<boolean>;

  /** Unity `IQuestConsolePlacementController.IsConsolePlacementMode`. */
  readonly isPlacementMode: boolean;

  /**
   * Unity `PlaneHeight` — the height the console is LIFTED to, accumulated by
   * {@link adjustHeight}. Every aim-driven move forces the stage's Y to it
   * (`QuestReposTool.TeleporationRaycast` overwrites `targetPosition.y` with
   * it), so it is the stage's Y in the interactive path.
   */
  readonly planeHeight: number;

  readonly pose: ConsoleStagePose;

  /**
   * The ADJUST phase: the stage has been put somewhere and the wearer has not
   * answered for it yet — Confirm keeps it, Revert goes back to aiming. **Web-first: Unity has no counterpart**, because upstream's only
   * interactive move IS the aim, so there is never a pose the aim disagrees
   * with and never anything to answer for.
   *
   * ## Placement is TWO phases, and this flag is the boundary
   *
   *  1. **Aim** — the ballistic ray picks a spot on the floor. A tap ends it.
   *  2. **Adjust** — the aim is SUSPENDED and the hands move the stage
   *     directly: one hand translates in three axes, two hands turn it. The
   *     wearer leaves by answering.
   *
   * A pinch-hold from phase 1 skips the tap and lands straight in phase 2,
   * which is the same thing said with one gesture instead of two.
   *
   * The suspension is the whole reason the phase has to exist rather than a
   * gesture simply committing: the next aimed frame would snap the stage back
   * onto the floor arc, silently discarding what the wearer just did.
   *
   * NOT the same thing as leaving placement mode: {@link isPlacementMode} stays
   * true throughout, so a workflow parked on `requires_console_placement` stays
   * parked (see `src/models/xr/CLAUDE.md`) and the mode's slate stays up.
   */
  readonly isAwaitingConfirmation: boolean;

  /** Unity `BeginPlacement` — captures the pose {@link resetPlacement} restores. */
  beginPlacement(): void;

  /** Unity `EndPlacement` — commits (the stage is already where the ghost was). */
  endPlacement(): void;

  /** Unity's `CancelDown` branch: `ResetPlacement()` then `EndPlacement()`. */
  cancelPlacement(): void;

  /**
   * Unity `Move(Vector3 originPos, Vector3 endPos)`: the aim's landing point.
   * `endPos.y` is ignored — Unity's caller has already forced it to
   * {@link planeHeight}, and the `originPos` argument is dead upstream (it is
   * assigned `PlaneHeight` and never read).
   */
  moveTo(x: number, z: number): void;

  /**
   * The stage's absolute pose during a free hand gesture — all three axes plus
   * the yaw, unlike {@link moveTo}, which takes its Y from {@link planeHeight}
   * and leaves the yaw alone.
   *
   * The yaw rides along rather than going through {@link rotate} because a
   * two-handed turn moves and turns the stage in the SAME frame: two writes
   * would emit twice and re-apply the stage transform twice for one gesture
   * frame. Single-handed callers pass the current yaw through unchanged.
   *
   * `y` is CLAMPED at the floor (0), which is the same floor
   * {@link adjustHeight} refuses to go below: the stage may be lifted off the
   * wearer's real floor but never sunk through it, so a gesture can never drive
   * the table and everything standing on it down through the room.
   *
   * The clamped `y` also BECOMES {@link planeHeight}, so a subsequent
   * aim-driven {@link moveTo} keeps the height the wearer dragged to — exactly
   * as if they had pressed a raise button that many times. Not doing this would
   * make the next aimed frame drop the stage back to the floor.
   *
   * Web-first; there is no Unity counterpart to any of the adjust API.
   */
  dragTo(x: number, y: number, z: number, yawDegrees: number): void;

  /**
   * Enter the adjust phase — the tap that ends the aim phase, or the release
   * that ends a hand gesture.
   */
  requestConfirmation(): void;

  /**
   * The slate's **"Reset"** — start this placement over: put the stage back
   * where it was when the mode opened and return to phase 1. Restores BOTH the
   * pose and {@link planeHeight}, unlike {@link resetPlacement}, which
   * reproduces upstream's deliberate desync; this has no Unity counterpart, so
   * it gets to be coherent.
   *
   * **The baseline is the MODE-OPEN pose, not the gesture's**, and that is what
   * keeps the button live in BOTH phases. An episode-scoped baseline would give
   * it nothing to do while the wearer is still aiming — a control that silently
   * does nothing, which `runtime/xr/CLAUDE.md` rules out — and a bar whose
   * buttons come and go between phases is exactly what made a wearer report the
   * Reset button as missing.
   *
   * **Distinct from {@link cancelPlacement}, and both are on the bar.** They
   * restore the same pose and differ in where they leave you: Reset keeps the
   * mode open and hands the ray back, Cancel closes it.
   */
  revertToAiming(): void;

  /**
   * Unity `Rotate(float deltaAngle)` — `m_consoleGhost.Rotate(Vector3.up, d)`.
   * Note the SIGN differs from upstream for the same numeric angle; see
   * {@link ConsoleStagePose}.
   */
  rotate(deltaDegrees: number): void;

  /** Unity `Move(float deltaHeight)` — clamped at floor level, like upstream. */
  adjustHeight(deltaHeight: number): void;

  /**
   * Unity `ResetHeight()` — zeroes the accumulator ONLY. The stage stays where
   * it is until the next {@link moveTo}, which drops it to the new plane. That
   * is upstream's behaviour, not an oversight.
   */
  resetHeight(): void;

  /** Unity `ResetPlacement()` — back to the pose captured at `BeginPlacement`. */
  resetPlacement(): void;

  /** Back to identity: session exit and recenter (see the interface header). */
  reset(): void;
}

export const CONSOLE_PLACEMENT_MODEL = Symbol.for("Campfire.ConsolePlacementModel");
